<template>
    <div class="NewCustomDropdown" :class="{ 'NewCustomDropdown--teleported': popoverTeleported }">
        <Popover
            :open="isPopoverOpen"
            :position-x="positionX"
            :position-y="positionY"
            :offset-x="offsetX"
            :offset-y="offsetY"
            :auto-flip="autoFlip"
            :flip-estimated-height="maxHeight + 50"
            :teleport="popoverTeleported"
            :portal-target="popoverTeleportTarget"
            :reposition-on-scroll="popoverRepositionOnScroll"
            @hide="closePopover"
        >
            <template #base>
                <slot
                    name="trigger"
                    :is-open="isPopoverOpen"
                    :toggle="togglePopover"
                    :open="openPopover"
                    :close="closePopover"
                >
                    <button
                        type="button"
                        class="NewCustomDropdown__defaultTrigger"
                        @click="togglePopover"
                    >
                        <span>{{ triggerLabel }}</span>
                    </button>
                </slot>
            </template>

            <div class="NewCustomDropdown__popover" :class="popoverClass">
                <div
                    v-if="enableSearch"
                    class="NewCustomDropdown__search"
                >
                    <div class="NewCustomDropdown__searchPrefix">
                        <EidIcon :icon-src="faMagnifyingGlass" />
                    </div>
                    <input
                        ref="searchInput"
                        class="NewCustomDropdown__searchInput"
                        type="text"
                        :placeholder="searchPlaceholder"
                        :value="searchQuery"
                        @input="handleSearchInput"
                    >
                </div>

                <div class="NewCustomDropdown__listContainer">
                    <q-virtual-scroll
                        ref="virtualScroll"
                        :items="filteredItems"
                        :item-size="itemSize"
                        class="NewCustomDropdown__virtualScroll"
                    >
                        <template #default="{ item, index }">
                            <div
                                :key="`${index}-${item.value}`"
                                class="NewCustomDropdown__option"
                                :class="{ 'NewCustomDropdown__option--focused': focusedIndex === index }"
                                @click="selectItem(item)"
                                @mouseenter="setFocusedIndex(index)"
                            >
                                <slot
                                    name="item"
                                    :item="item"
                                    :index="index"
                                >
                                    <span>{{ item.label }}</span>
                                </slot>
                            </div>
                        </template>
                    </q-virtual-scroll>

                    <div
                        v-if="filteredItems.length === 0"
                        class="NewCustomDropdown__noResults"
                    >
                        {{ noResultsText }}
                    </div>
                </div>
            </div>
        </Popover>
    </div>
</template>

<style lang="scss">
$eidSecureBlue-10: var(--eidSecureBlue-10);
$border-color: $eidSecureBlue-10;
$border-radius: 8.75px;
$eidWhite: #ffffff;
$eidTrustGreen-75: var(--eidTrustGreen-75);
$eidQualityBlack-50: var(--eidQualityBlack-50);
$eidQualityBlack-75: var(--eidQualityBlack-75);

.NewCustomDropdown {
    .Popover {
        position: relative;
    }

    &__defaultTrigger {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 0.5rem 1rem;
        border-radius: $border-radius;
        border: 1px solid $border-color;
        background-color: $eidTrustGreen-75;
        color: $eidWhite;
        cursor: pointer;
        font-size: 14px;
        font-weight: 600;

        &:hover {
            opacity: 0.9;
        }
    }

    &__popover {
        background: $eidWhite;
        border: 1px solid $border-color;
        border-radius: $border-radius;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        width: 400px;
    }

    &__search {
        display: flex;
        align-items: center;
        padding: 8px 12px;
        border-bottom: 1px solid $border-color;
        border-top-left-radius: $border-radius;
        border-top-right-radius: $border-radius;
        position: relative;
    }

    &__searchPrefix {
        display: flex;
        align-items: center;
        margin-right: 8px;
        position: absolute;
        left: 20px;
        height: 100%;
        color: $eidQualityBlack-75;
    }

    &__searchInput {
        flex: 1;
        border: 1px solid $border-color;
        border-radius: $border-radius;
        outline: none;
        background: transparent;
        font-size: 14px;
        padding: 4px 0 4px 30px;
        width: 100%;

        &::placeholder {
            color: $eidQualityBlack-50;
        }
    }

    &__listContainer {
        max-height: var(--newCustomDropdownMaxHeight, 300px);
        overflow: hidden;
    }

    &__virtualScroll {
        height: 100%;
        max-height: var(--newCustomDropdownMaxHeight, 300px);
    }

    &__option {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        cursor: pointer;
        border-bottom: 1px solid $border-color;
        transition: background-color 0.15s ease;

        svg {
            color: $eidTrustGreen-75;
        }

        &:hover,
        &--focused {
            background-color: $eidTrustGreen-75;
            color: $eidWhite;

            svg {
                color: $eidWhite;
            }
        }

        &:last-child {
            border-bottom: none;
        }

        span {
            font-size: 14px;
            font-weight: 500;
        }
    }

    &__noResults {
        padding: 24px 16px;
        text-align: center;
        color: $eidQualityBlack-50;
        font-size: 14px;
        font-style: italic;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { QVirtualScroll } from 'quasar';

    import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import Popover from '@evidentid/dashboard-commons/components/Popover/Popover.vue';

    import { DropdownOption } from './types';

    function sanitizeSearchValue(val: string): string {
        return val.replace(/[\s\p{P}]/gu, '');
    }

    export default defineComponent({
        name: 'NewCustomDropdown',
        components: {
            QVirtualScroll,
            Popover,
            EidIcon,
        },
        props: {
            items: {
                type: Array as PropType<DropdownOption<unknown>[]>,
                default: (): DropdownOption<unknown>[] => [],
            },
            itemSize: {
                type: Number,
                default: 48,
            },
            searchPlaceholder: {
                type: String,
                default: 'Search...',
            },
            noResultsText: {
                type: String,
                default: 'No results found',
            },
            triggerLabel: {
                type: String,
                default: 'Select option',
            },
            enableSearch: {
                type: Boolean,
                default: true,
            },
            maxHeight: {
                type: Number,
                default: 300,
            },
            closeOnSelect: {
                type: Boolean,
                default: true,
            },
            popoverClass: {
                type: String,
                default: '',
            },
            /**
             * Horizontal alignment of popover relative to trigger.
             */
            positionX: {
                type: String as PropType<'start' | 'end'>,
                default: 'start',
            },
            /**
             * Vertical position of popover relative to trigger.
             */
            positionY: {
                type: String as PropType<'top' | 'bottom'>,
                default: 'bottom',
            },
            /**
             * Horizontal offset in pixels.
             * CSS convention: positive = shift right, negative = shift left
             */
            offsetX: {
                type: Number,
                default: 0,
            },
            /**
             * Vertical offset in pixels.
             * CSS convention: positive = shift down, negative = shift up
             */
            offsetY: {
                type: Number,
                default: 0,
            },
            /**
             * Auto-flip behavior when popover doesn't fit in viewport.
             * 'x' = horizontal only, 'y' = vertical only, 'xy' = enable both
             * null = no auto-flipping
             */
            autoFlip: {
                type: String as PropType<'x' | 'y' | 'xy' | null>,
                default: null,
            },
            popoverTeleported: {
                type: Boolean,
                default: false,
            },
            popoverTeleportTarget: {
                type: String,
                default: 'default-popover-target',
            },
            // Default off to save performance - only enable when teleported popover needs to follow scroll
            popoverRepositionOnScroll: {
                type: Boolean,
                default: false,
            },
        },
        emits: [ 'close', 'open', 'select' ],
        data() {
            return {
                isPopoverOpen: false,
                searchQuery: '',
                focusedIndex: -1,
                searchRegex: null as RegExp | null,
                faMagnifyingGlass,
            };
        },
        computed: {
            filteredItems(): DropdownOption<unknown>[] {
                if (!this.searchRegex) {
                    return this.items;
                }

                return this.items.filter(({ label }) =>
                    this.searchRegex?.test(sanitizeSearchValue(String(label))),
                );
            },
        },
        watch: {
            isPopoverOpen(newValue: boolean) {
                if (newValue) {
                    this.$emit('open');
                    this.$nextTick(() => {
                        const searchInput = this.$refs.searchInput as HTMLInputElement;
                        if (searchInput && this.enableSearch) {
                            searchInput.focus();
                        }
                    });
                } else {
                    this.$emit('close');
                    this.resetSearch();
                }
            },
            maxHeight(newValue: number) {
                this.updateMaxHeightCssVar(newValue);
            },
        },
        mounted() {
            this.updateMaxHeightCssVar(this.maxHeight);
        },
        methods: {
            togglePopover(): void {
                this.isPopoverOpen = !this.isPopoverOpen;
            },
            openPopover(): void {
                this.isPopoverOpen = true;
            },
            closePopover(): void {
                this.isPopoverOpen = false;
            },
            handleSearchInput(event: InputEvent): void {
                if (!this.enableSearch) {
                    return;
                }

                const target = event.target as HTMLInputElement;
                this.searchQuery = target.value;
                this.updateSearchFilter();
                this.focusedIndex = -1;
            },
            updateSearchFilter(): void {
                if (this.searchQuery.trim()) {
                    const sanitizedQuery = sanitizeSearchValue(this.searchQuery);
                    this.searchRegex = new RegExp(sanitizedQuery, 'iu');
                } else {
                    this.searchRegex = null;
                }
            },
            resetSearch(): void {
                this.searchQuery = '';
                this.searchRegex = null;
                this.focusedIndex = -1;
            },
            selectItem(option: DropdownOption<unknown>): void {
                this.$emit('select', option);
                if (this.closeOnSelect) {
                    this.closePopover();
                }
            },
            setFocusedIndex(index: number): void {
                this.focusedIndex = index;
            },
            updateMaxHeightCssVar(value: number): void {
                const rootElement = this.$el as HTMLElement;
                rootElement.style.setProperty('--newCustomDropdownMaxHeight', `${value}px`);
            },
        },
    });
</script>

